import { ZERO_COST } from 'constants/currency';
import { cloneDeep as lodashCloneDeep } from 'lodash-es';
import { Duration as LuxonDuration } from 'luxon';
import type { Currency } from 'types/account';
import type { Cost } from 'types/units';

export const timestamp = () => {
  return Math.floor(Date.now() / 1000);
};

export const timestampMs = () => {
  return Date.now();
};

export function calculateCost(totalHours = 0, hourlyRate = 0, currency?: Currency): Cost {
  if (totalHours === 0 && currency == null) {
    // Avoid LuxonDuration call in base case
    return ZERO_COST;
  }

  const baseDuration = LuxonDuration.fromObject({ hours: totalHours });

  const prefix = currency && currency.symbol_first ? currency.symbol : '';
  const postfix = currency && !currency.symbol_first ? currency.symbol : '';

  const totalMinutes = baseDuration.as('minutes');
  const roundedTotalHours = Math.floor(totalMinutes) / 60;
  const amount = roundedTotalHours * hourlyRate;
  const amountWithDecimals = amount.toFixed(2);

  return {
    formatted: `${prefix}${amountWithDecimals}${postfix}`,
    amount: parseFloat(amountWithDecimals),
    fractional: Math.floor(amount * 100),
    currency_code: currency?.iso_code ?? '',
  };
}

export function removeDuplicateIdsFromArray<T extends { id: number }>(
  array: Array<T> = []
): Array<T> {
  const existingIds: Array<number> = [];
  return array.filter(item => {
    // If id exists in array, skip it:
    if (existingIds.includes(item.id)) {
      return false;
    }
    // Keep track of the id and keep it in the array:
    existingIds.push(item.id);
    return true;
  });
}

export const isPromiseLike = <T>(value: any): value is PromiseLike<T> => {
  return typeof value.then === 'function';
};

/**
 * Uses `structuredClone` in runtimes that support it, and falls back to Lodash `cloneDeep`
 * otherwise.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/structuredClone
 */
export const cloneDeep: <T>(value: T) => T =
  'structuredClone' in globalThis ? (globalThis as any).structuredClone : lodashCloneDeep;
